import PropTypes from 'prop-types'
import { forwardRef, memo, useMemo } from 'react'
import BorderHighlight from 'components/elements/BorderHighlight/BorderHighlight'
import classNames from 'lib/util/classNames'
import Icon from '../Icon/Icon'
import styles from './Button.module.scss'

/**
 * The `ButtonBasic`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
const ButtonBasic = forwardRef((props, forwardedRef) => {
  const {
    ElementTag,
    children,
    className,
    icon,
    isBusy,
    isHover,
    side,
    useHighlight,
    useHighlightHover,
    ...rest
  } = props
  const attributes = useMemo(
    () => ({
      className: classNames([
        styles.button,
        styles.buttonTypeBasic,
        className,
        !isBusy && icon !== '' && styles[side],
        !isBusy && icon !== '' && styles.hasIcon
      ])
    }),
    [className, side, icon, isBusy]
  )

  return (
    <ElementTag ref={forwardedRef} {...attributes} {...rest}>
      {icon && !isBusy && (
        <div className={styles.buttonSymbol}>
          <Icon icon={icon} />
        </div>
      )}
      {(useHighlight || useHighlightHover) && (
        <BorderHighlight
          isActive={useHighlight ? !isHover : isHover}
          animateOnMobile={true}
        />
      )}
      <span className={styles.text}>{children}</span>
    </ElementTag>
  )
})

// Display name
ButtonBasic.displayName = 'ButtonBasic'

/** @type {object} */
ButtonBasic.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  ElementTag: PropTypes.elementType.isRequired,
  icon: PropTypes.string,
  side: PropTypes.string,
  isBusy: PropTypes.bool,
  isHover: PropTypes.bool,
  useHighlight: PropTypes.bool,
  useHighlightHover: PropTypes.bool
}

/** @type {object} */
ButtonBasic.defaultProps = {
  children: [],
  className: '',
  icon: '',
  side: 'left',
  isBusy: false,
  isHover: false,
  useHighlight: false,
  useHighlightHover: false
}

// Memoize
export default memo(ButtonBasic)
